import { useDrag, UserDragConfig } from '@use-gesture/react'
import { RefObject, useContext } from 'react'

import { ControllerContext } from '@Components/Product/Slider/Context/ControllerContext'

interface DragMemo {
  mx: any
  originalTransition: string
  originalTranslateX: string
}

const DRAG_SCREEN_THRESHOLD = 0.2

export function DragNext<T extends HTMLElement>(
  target: RefObject<T>,
  goToNextSlide: (recalculate: boolean) => void,
  goToPreviousSlide: (recalculate: boolean) => void,
  axis?: UserDragConfig['axis'],
) {
  const { isZoomedIn } = useContext(ControllerContext)

  useDrag(
    ({
      first,
      last,
      movement: [mx],
      xy: [x, y],
      memo = {},
      tap,
      cancel,
    }) => {
      if (isZoomedIn || tap) {
        return memo
      }
      const next: DragMemo = {
        originalTransition: memo?.originalTransition || '',
        originalTranslateX: memo?.originalTranslateX || '',
        mx,
      }
      if (!target?.current) {
        cancel()
        return memo
      }

      if (first) {
        const result = target.current.style.transform.match(/translateX\((.*?)\)/)
        next.originalTranslateX = result && result.length > 1 ? result[1] : '0%'
        next.originalTransition = target.current.style.transition
        // eslint-disable-next-line no-param-reassign
        target.current.style.transition = ''
      }

      const bounds = target.current.parentElement?.getBoundingClientRect()
      if (!bounds) {
        return next
      }

      if (x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) {
        cancel()
      }

      if (!last) {
        // eslint-disable-next-line no-param-reassign
        target.current.style.transform = `translateX(calc(${next.originalTranslateX} + ${mx}px))`
        return next
      }

      if (last) {
        // eslint-disable-next-line no-param-reassign
        target.current.style.transition = next.originalTransition
        if (memo.mx > (window.innerWidth * DRAG_SCREEN_THRESHOLD)) {
          goToPreviousSlide(true)
        } else if (memo.mx < -(window.innerWidth * DRAG_SCREEN_THRESHOLD)) {
          goToNextSlide(true)
        } else {
          // eslint-disable-next-line no-param-reassign
          target.current.style.transform = `translateX(${next.originalTranslateX})`
        }
      }

      return next
    },
    {
      axis,
      axisThreshold: { pen: 16, mouse: 10, touch: 10 },
      target,
      filterTaps: true,
    },
  )
}
